import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Ticket, Crown, Sparkles, Bell, QrCode, Database, Megaphone, ArrowRight, Trophy } from "lucide-react";
import { Button } from "@/components/ui/button";

const B2C_TICKETS = [
  { label: "50% OFF", title: "Café + pan", from: "#FF007A", to: "#ff6ab3", rot: -8, x: 0, y: 0, delay: 0 },
  { label: "-$150", title: "Corte de cabello", from: "#0055FF", to: "#4d8bff", rot: 6, x: 42, y: 26, delay: 0.15 },
  { label: "GRATIS", title: "Postre del día", from: "#f59e0b", to: "#fcd34d", rot: -3, x: 12, y: 58, delay: 0.3 },
];
const B2B_TICKETS = [
  { label: "2x1", title: "Martes flojo", from: "#0055FF", to: "#4d8bff", rot: -7, x: 0, y: 0, delay: 0 },
  { label: "20% OFF", title: "Bienvenida", from: "#FF007A", to: "#ff6ab3", rot: 5, x: 42, y: 26, delay: 0.15 },
  { label: "-$100", title: "Clientes fieles", from: "#10b981", to: "#6ee7b7", rot: -2, x: 12, y: 58, delay: 0.3 },
];

function useCounter(target, duration = 1600) {
  const [v, setV] = useState(0);
  useEffect(() => {
    let raf; const t0 = performance.now();
    const tick = (t) => { const p = Math.min(1, (t - t0) / duration); setV(Math.round(target * (1 - Math.pow(1 - p, 3)))); if (p < 1) raf = requestAnimationFrame(tick); };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return v;
}

function FloatingTickets({ tickets }) {
  return (
    <div className="relative w-[210px] h-[190px] shrink-0 mx-auto sm:mx-0" aria-hidden>
      {tickets.map((t, i) => (
        <motion.div
          key={t.title}
          initial={{ opacity: 0, y: 30, rotate: t.rot - 10, scale: 0.8 }}
          animate={{ opacity: 1, y: [0, -6, 0], rotate: t.rot, scale: 1 }}
          transition={{ opacity: { delay: t.delay, duration: 0.5 }, scale: { delay: t.delay, duration: 0.5 }, rotate: { delay: t.delay, duration: 0.5 }, y: { delay: t.delay + 0.6, duration: 3 + i * 0.4, repeat: Infinity, ease: "easeInOut" } }}
          className="absolute w-[150px] rounded-2xl p-3 text-white shadow-2xl"
          style={{ left: t.x, top: t.y, background: `linear-gradient(135deg, ${t.from}, ${t.to})`, zIndex: 10 - i }}
        >
          <div className="flex items-center justify-between text-[8px] font-black uppercase tracking-widest opacity-90"><span>Cupón</span><Ticket className="w-3 h-3" /></div>
          <div className="font-heading font-black text-2xl leading-none mt-1 drop-shadow">{t.label}</div>
          <div className="text-[10px] font-bold mt-0.5 truncate">{t.title}</div>
          <div className="mt-2 h-4 rounded-md bg-white/90 text-black text-[8px] font-mono font-black tracking-[0.2em] grid place-items-center">CUP-{["7K2M9A", "X4Q8ZP", "N3R6WD"][i]}</div>
          <span className="absolute -left-2 top-[58%] w-4 h-4 rounded-full bg-[#0A0A0A]" /><span className="absolute -right-2 top-[58%] w-4 h-4 rounded-full bg-[#0A0A0A]" />
        </motion.div>
      ))}
      <motion.div initial={{ scale: 0, rotate: -30 }} animate={{ scale: 1, rotate: 12 }} transition={{ delay: 0.7, type: "spring", stiffness: 200 }} className="absolute -right-2 -top-3 w-12 h-12 rounded-full grid place-items-center shadow-xl" style={{ background: "linear-gradient(135deg,#fff3b0,#f7c948 50%,#d69e2e)", zIndex: 20 }}>
        <Crown className="w-6 h-6 text-[#2b1a00]" />
      </motion.div>
    </div>
  );
}

/** Callout espectacular del sistema de cupones para el hero. variant: "b2c" | "b2b" */
export default function CouponHeroCallout({ variant = "b2c" }) {
  const b2c = variant === "b2c";
  const saved = useCounter(b2c ? 1240 : 3860);
  const accent = b2c ? "#FF007A" : "#0055FF";

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
      className="mt-4 relative rounded-3xl p-[2px] max-w-xl overflow-hidden"
      style={{ background: "linear-gradient(120deg,#FF007A,#f7c948,#0055FF,#FF007A)", backgroundSize: "300% 300%", animation: "hr-border-flow 6s ease infinite" }}
      data-testid={`hero-coupons-${variant}-callout`}
    >
      <style>{`@keyframes hr-border-flow{0%{background-position:0% 50%}50%{background-position:100% 50%}100%{background-position:0% 50%}}@keyframes hr-shine{0%{transform:translateX(-120%) skewX(-20deg)}100%{transform:translateX(260%) skewX(-20deg)}}`}</style>
      <div className="relative rounded-[22px] bg-[#0A0A0A] text-white p-5 sm:p-6 overflow-hidden">
        <div className="absolute -top-20 -left-16 w-56 h-56 rounded-full blur-3xl opacity-40 pointer-events-none" style={{ background: accent }} />
        <div className="absolute -bottom-24 -right-10 w-64 h-64 rounded-full bg-[#f7c948]/25 blur-3xl pointer-events-none" />
        <div className="absolute inset-y-0 w-1/3 bg-gradient-to-r from-transparent via-white/10 to-transparent pointer-events-none" style={{ animation: "hr-shine 4.5s ease-in-out infinite" }} />

        <div className="relative flex items-center gap-2 mb-3">
          <span className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[10px] font-black uppercase tracking-[0.2em] text-white" style={{ background: accent }}><Sparkles className="w-3 h-3" /> Nuevo · Cupones</span>
          <span className="inline-flex items-center gap-1 rounded-full bg-[#f7c948] text-[#2b1a00] px-3 py-1 text-[10px] font-black uppercase tracking-[0.2em]"><Trophy className="w-3 h-3" /> Cupón de Oro</span>
        </div>

        <div className="relative grid sm:grid-cols-[1fr_auto] gap-5 items-center">
          <div>
            {b2c ? (
              <>
                <h3 className="font-heading font-black text-2xl sm:text-3xl leading-[1.05] tracking-tight">
                  Escanea el QR de tu tienda favorita y <span className="text-transparent bg-clip-text" style={{ backgroundImage: "linear-gradient(90deg,#FF007A,#f7c948)" }}>llénate de cupones.</span>
                </h3>
                <p className="mt-3 text-sm text-white/75 leading-relaxed">
                  Cada comercio tiene su QR. Lo escaneas <strong className="text-white">una sola vez</strong> y desde ese momento te regalan cupones directo a tu <strong className="text-white">CUPONERA</strong>: descuentos, 2x1 y regalos con tu nombre, avisos al celular cuando llegan y cuando están por vencer. Y cada mes, quien use cupones en más comercios se lleva el <strong className="text-[#f7c948]">CUPÓN DE ORO</strong> 🏆.
                </p>
              </>
            ) : (
              <>
                <h3 className="font-heading font-black text-2xl sm:text-3xl leading-[1.05] tracking-tight">
                  Tu propia base de clientes. <span className="text-transparent bg-clip-text" style={{ backgroundImage: "linear-gradient(90deg,#4d8bff,#f7c948)" }}>Cupones que los hacen volver.</span>
                </h3>
                <p className="mt-3 text-sm text-white/75 leading-relaxed">
                  Imprime <strong className="text-white">tu QR personalizado</strong>: cada cliente que lo escanea queda en una base de datos <strong className="text-white">100% tuya</strong> y recibe al instante un cupón de bienvenida. Después lanzas campañas cuando quieras — un martes flojo, para mover inventario, para premiar fieles — con cupones <strong className="text-white">diseñados por IA</strong> y su nombre. Llegan por push a su celular. Tú canjeas en caja y ves la venta que generó cada campaña.
                </p>
              </>
            )}

            <div className="mt-4 grid grid-cols-3 gap-2">
              {(b2c
                ? [[QrCode, "1 escaneo", "y ya estás dentro"], [Bell, "Avisos push", "cupones nuevos y por vencer"], [Crown, "Cupón de Oro", "premio cada mes"]]
                : [[Database, "Base propia", "cada escaneo = 1 cliente"], [Megaphone, "Campañas", "por segmento, con nombre"], [Crown, "Cupón de Oro", "tus clientes compiten"]]
              ).map(([Icon, t, d]) => (
                <div key={t} className="rounded-xl bg-white/[0.06] border border-white/10 p-2.5">
                  <Icon className="w-4 h-4 text-[#f7c948]" />
                  <div className="text-xs font-extrabold mt-1 leading-tight">{t}</div>
                  <div className="text-[10px] text-white/60 leading-tight">{d}</div>
                </div>
              ))}
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-3">
              <div className="rounded-2xl px-4 py-2" style={{ background: "linear-gradient(135deg,#fff3b0,#f7c948 50%,#d69e2e)" }}>
                <div className="text-[9px] uppercase tracking-widest font-black text-[#2b1a00]/70">{b2c ? "Ahorro promedio al mes" : "Venta extra promedio / mes"}</div>
                <div className="font-heading font-black text-2xl leading-none text-[#2b1a00]" data-testid={`hero-coupons-${variant}-counter`}>${saved.toLocaleString("es-MX")} <span className="text-xs">MXN</span></div>
              </div>
              {b2c ? (
                <Link to="/cuponera" data-testid="hero-coupons-b2c-cta">
                  <Button className="h-11 rounded-full font-bold text-white shadow-lg hover:scale-[1.03] transition-transform" style={{ background: accent }}><Ticket className="w-4 h-4" /> Abrir mi Cuponera <ArrowRight className="w-4 h-4" /></Button>
                </Link>
              ) : (
                <a href="#pricing" data-testid="hero-coupons-b2b-cta">
                  <Button className="h-11 rounded-full font-bold text-white shadow-lg hover:scale-[1.03] transition-transform" style={{ background: accent }}><Ticket className="w-4 h-4" /> Paquete Cupones desde $299 <ArrowRight className="w-4 h-4" /></Button>
                </a>
              )}
            </div>
          </div>

          <FloatingTickets tickets={b2c ? B2C_TICKETS : B2B_TICKETS} />
        </div>
      </div>
    </motion.div>
  );
}
